import {
  canonicalToProject,
  projectToCanonical
} from './spatial-frame.js';

export const RENDER_FRAME_PROJECTED = 'PROJECTED-LOCAL';
export const RENDER_FRAME_ENU = 'ENU';
export const DEFAULT_MAX_ENU_SPAN_METRES = 250000;

function finite(value) {
  return Number.isFinite(Number(value));
}

export function projectedBoundsToRender(bounds, projectOrigin) {
  if (!bounds || !projectOrigin) return null;
  const values = [
    bounds.minX,
    bounds.minY,
    bounds.maxX,
    bounds.maxY,
    projectOrigin.x,
    projectOrigin.y
  ];
  if (!values.every(finite)) return null;
  return {
    minX: Number(bounds.minX) - Number(projectOrigin.x),
    minY: Number(bounds.minY) - Number(projectOrigin.y),
    minZ: Number(bounds.minZ) || 0,
    maxX: Number(bounds.maxX) - Number(projectOrigin.x),
    maxY: Number(bounds.maxY) - Number(projectOrigin.y),
    maxZ: Number(bounds.maxZ) || 0
  };
}

export function enuBoundsToRender(bounds) {
  if (!bounds || !Object.values(bounds).every(finite)) return null;
  return {
    minX: Number(bounds.minEast),
    minY: Number(bounds.minNorth),
    minZ: Number(bounds.minUp),
    maxX: Number(bounds.maxEast),
    maxY: Number(bounds.maxNorth),
    maxZ: Number(bounds.maxUp)
  };
}

export function combineRenderBounds(boundsList) {
  const valid = boundsList.filter(Boolean);
  if (!valid.length) return null;
  return valid.reduce((combined, bounds) => ({
    minX: Math.min(combined.minX, bounds.minX),
    minY: Math.min(combined.minY, bounds.minY),
    minZ: Math.min(combined.minZ, bounds.minZ),
    maxX: Math.max(combined.maxX, bounds.maxX),
    maxY: Math.max(combined.maxY, bounds.maxY),
    maxZ: Math.max(combined.maxZ, bounds.maxZ)
  }));
}

export function chooseSpatialRenderFrame({
  requestedFrame = RENDER_FRAME_PROJECTED,
  layers = [],
  additionalBounds = [],
  spatialFrame = null,
  hasSpatialBlockers = false,
  maxEnuSpanMetres = DEFAULT_MAX_ENU_SPAN_METRES
} = {}) {
  if (requestedFrame !== RENDER_FRAME_ENU) {
    return { frame: RENDER_FRAME_PROJECTED, reason: 'projected-requested' };
  }
  if (!spatialFrame) {
    return { frame: RENDER_FRAME_PROJECTED, reason: 'missing-spatial-frame' };
  }
  if (hasSpatialBlockers) {
    return { frame: RENDER_FRAME_PROJECTED, reason: 'project-has-unadapted-spatial-content' };
  }
  if (!layers.length && !additionalBounds.length) {
    return { frame: RENDER_FRAME_PROJECTED, reason: 'no-spatial-content' };
  }
  if (layers.some(layer => layer.sourceCrsStatus !== 'resolved')) {
    return { frame: RENDER_FRAME_PROJECTED, reason: 'unverified-vector-layer' };
  }
  if (layers.some(layer =>
    layer.spatialShadowStatus !== 'ready' ||
    !layer.spatialShadow?.validation?.passed
  )) {
    return { frame: RENDER_FRAME_PROJECTED, reason: 'shadow-not-ready' };
  }
  const bounds = combineRenderBounds([
    ...layers.map(layer => enuBoundsToRender(layer.spatialShadow.bounds)),
    ...additionalBounds.map(enuBoundsToRender)
  ]);
  if (!bounds) {
    return { frame: RENDER_FRAME_PROJECTED, reason: 'missing-shadow-bounds' };
  }
  const span = Math.max(
    bounds.maxX - bounds.minX,
    bounds.maxY - bounds.minY
  );
  if (!Number.isFinite(span) || span > maxEnuSpanMetres) {
    return {
      frame: RENDER_FRAME_PROJECTED,
      reason: 'enu-span-too-large',
      span
    };
  }
  return { frame: RENDER_FRAME_ENU, reason: 'eligible', span, bounds };
}

export function projectCoordinatesToRender(coordinates, options = {}) {
  if (!Array.isArray(coordinates)) return null;
  const rendered = coordinates.map(coordinate => {
    if (!Array.isArray(coordinate)) return null;
    return projectPointToRender({
      x: Number(coordinate[0]),
      y: Number(coordinate[1]),
      z: Number(coordinate[2]) || 0
    }, options);
  });
  return rendered.every(Boolean) ? rendered : null;
}

export function projectPointToRender(point, {
  renderFrame = RENDER_FRAME_PROJECTED,
  projectOrigin,
  spatialFrame
} = {}) {
  if (!point || !finite(point.x) || !finite(point.y)) return null;
  if (renderFrame === RENDER_FRAME_ENU) {
    const canonical = projectToCanonical(
      Number(point.x),
      Number(point.y),
      Number(point.z) || 0,
      spatialFrame
    );
    return canonical
      ? { x: canonical.enu.east, y: canonical.enu.north, z: canonical.enu.up }
      : null;
  }
  if (!projectOrigin) return null;
  return {
    x: Number(point.x) - Number(projectOrigin.x),
    y: Number(point.y) - Number(projectOrigin.y),
    z: Number(point.z) || 0
  };
}

export function renderPointToProject(point, {
  renderFrame = RENDER_FRAME_PROJECTED,
  projectOrigin,
  spatialFrame
} = {}) {
  if (!point || !finite(point.x) || !finite(point.y)) return null;
  if (renderFrame === RENDER_FRAME_ENU) {
    return canonicalToProject({
      east: Number(point.x),
      north: Number(point.y),
      up: Number(point.z) || 0
    }, spatialFrame);
  }
  if (!projectOrigin) return null;
  return {
    x: Number(point.x) + Number(projectOrigin.x),
    y: Number(point.y) + Number(projectOrigin.y),
    z: Number(point.z) || 0
  };
}
